// Image target event bridge — isolated from UI and state machine rendering.
// Translates XR8 reality.image* events into named app state transitions.

import { logger } from '../utils/logger';
import { anchorAcquired, getState } from '../state/appState';
import { appConfig } from '../config/appConfig';
import { applyWhalePlacement, isStabilizationActive, startStabilizationWindow } from '../scene/whalePlacement';
import { updateDebugAnchorCube } from '../scene/debugAnchorCube';
import { updatePosterOverlay, hidePosterOverlay } from '../scene/posterOverlay';
import { getImageTargetWorldWidth } from '../utils/imageTargetScale';

interface ImageTargetDetail {
  name: string;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number; w: number };
  scale: number;
  scaledWidth?: number;
  scaledHeight?: number;
}


/**
 * Returns an XR8 pipeline module that bridges image target events into the
 * app state machine. Register this module before calling XR8.run().
 *
 * Guards state transitions: only fires anchorAcquired() from SCANNING,
 * SCAN_GUIDANCE, or TRACKING_LOST. Whale pose is updated every frame while the
 * sign is visible; on image loss the whale stays at its last placed position.
 */
export function imageTargetPipelineModule(): XR8PipelineModule {
  const fmt = (n: number): string => n.toFixed(3);
  const fmtPosition = (position: ImageTargetDetail['position']): string =>
    `${fmt(position.x)}, ${fmt(position.y)}, ${fmt(position.z)}`;

  const onFound = (detail: ImageTargetDetail) => {
    if (detail.name !== appConfig.imageTargetName) return;
    updateDebugAnchorCube(detail);
    updatePosterOverlay(detail);
    applyWhalePlacement(detail);
    const state = getState();
    logger.log(
      'Image target found/reacquired:',
      detail.name,
      `state=${state}`,
      `targetPos=(${fmtPosition(detail.position)})`,
      `targetScale=${fmt(detail.scale)}`,
      `targetWidth=${fmt(getImageTargetWorldWidth(detail))} world units`,
      `configuredWidth=${fmt(appConfig.imageTargetPhysicalWidthM)}m`,
    );
    if (state === 'SCANNING' || state === 'SCAN_GUIDANCE' || state === 'TRACKING_LOST') {
      anchorAcquired();
    } else if (state === 'WHALE_VISIBLE' || state === 'INFO_MODAL_OPEN') {
      // Re-open the stabilization window so the next few seconds of imageupdated
      // events refine the whale's world position against the freshly-detected
      // target pose. This corrects any SLAM scale drift that accumulated since
      // the target was last visible.
      startStabilizationWindow();
    }
  };

  const onLost = (detail: { name: string }) => {
    if (detail.name !== appConfig.imageTargetName) return;
    hidePosterOverlay();
    // Whale and debug anchor cube stay at their last world positions until the
    // sign re-enters view; SLAM should move the camera relative to them.
    logger.log('Image target left frame:', detail.name);
  };

  return {
    name: 'image-target-manager',
    listeners: [
      {
        event: 'reality.imagefound',
        process: ({ detail }: { detail: unknown }) =>
          onFound(detail as ImageTargetDetail),
      },
      {
        event: 'reality.imageupdated',
        process: ({ detail }: { detail: unknown }) => {
          const d = detail as ImageTargetDetail;
          if (d.name !== appConfig.imageTargetName) return;
          updateDebugAnchorCube(d);
          // Only update whale placement while world position is being established
          // or freshly re-calibrated:
          //   • ANCHORED_AWAITING_AIM — pre-reveal; track sign continuously.
          //   • stabilization window  — opened at initial reveal and reopened on
          //     every subsequent imagefound, so each re-acquisition gets a brief
          //     settle period to correct any SLAM scale drift.
          // Outside these windows, SLAM drives the Three.js camera through world
          // space; a whale at a fixed Three.js position stays correctly anchored.
          const state = getState();
          updatePosterOverlay(d);
          if (state === 'ANCHORED_AWAITING_AIM' || isStabilizationActive()) {
            applyWhalePlacement(d);
          }
        },
      },
      {
        event: 'reality.imagelost',
        process: ({ detail }: { detail: unknown }) =>
          onLost(detail as { name: string }),
      },
    ],
  };
}
